import { DT } from "../../constants.ts";
import { add, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, explode, hitscan, isEnemy, playersInRadius, spawnProjectile, spawnZone } from "../../sim/combat.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, ownerAlive, ready, secs, simpleProjectile, startMove, toGround, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { fleet: 0, scoped: 1, plasmaCharge: 2, edgeWindow: 3 } as const;
export const PK = { Plasma: pk(HeroId.BlackWindow, 0) } as const;
export const ZK = { PlasmaPool: zk(HeroId.BlackWindow, 0) } as const;
export const FX = { Rifle: fxk(HeroId.BlackWindow, 0), Plasma: fxk(HeroId.BlackWindow, 1), Kick: fxk(HeroId.BlackWindow, 2) } as const;

const def: HeroDef = {
  id: HeroId.BlackWindow,
  slug: "black-window",
  name: "Black Window",
  role: Role.Damage,
  color: "#c81e5a",
  blurb: "Long-angle spy marksman with rifle, batons, evasive footwork and disabling kicks.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.38,
  height: 1.75,
  headRadius: 0.2,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 15,
  reloadTime: 1.5,
  ultCost: 2800,
  ultGain: { damage: 0.7 },
  abilities: {
    [Slot.Primary]: { name: "Red Ledger Rifle", key: "LMB", description: "Fire the rifle, or snap to shock batons up close.", cooldown: 0 },
    [Slot.Secondary]: { name: "Straight Ledger", key: "RMB (hold)", description: "Scope in; primary fires a high-power sniper shot.", cooldown: 0 },
    [Slot.Ability1]: { name: "Fleet of Footnotes", key: "Shift", description: "Spend stamina to sprint; jumping spends a chunk for a high leap.", cooldown: 0 },
    [Slot.Ability2]: { name: "Edge Closer", key: "E", description: "Kick enemies up; a hit opens a short second-kick dash window.", cooldown: 15 },
    [Slot.Ability3]: { name: "Bite Drop", key: "F", description: "Aim, then LMB to leap at the targeted area and slam nearby enemies.", cooldown: 15 },
    [Slot.Ultimate]: { name: "Windowbreaker Plasma", key: "Q (hold)", description: "Charge a destruction shot that explodes and leaves slowing plasma.", cooldown: 0 },
  },
  passive: { name: "Close Work", description: "Primary automatically uses batons against enemies within 3 meters." },
};

function kick(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, range: number, stun = 0) {
  const c = sim.c;
  const p = add(sim.posOf(eid), { x: 0, y: 0.9, z: 0 });
  let hit = false;
  for (const { eid: t } of playersInRadius(sim, p, range, (e) => isEnemy(sim, eid, e), false)) {
    applyDamage(sim, t, 35, eid, { pos: add(sim.posOf(t), { x: 0, y: 1, z: 0 }) });
    c.Vel.y[t] = Math.max(c.Vel.y[t]!, 7);
    c.Player.grounded[t] = 0;
    if (stun > 0) applyStatus(sim, t, "stun", stun, { source: eid });
    hit = true;
  }
  if (hit) sim.emit({ type: "explosion", pos: p, radius: range, kind: FX.Kick });
  return hit;
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.fleet, 120);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const fleet = hv(c, eid, V.fleet);
      setHv(c, eid, V.fleet, Math.min(120, fleet + 30 * DT));
      if (hv(c, eid, V.edgeWindow) > 0) setHv(c, eid, V.edgeWindow, hv(c, eid, V.edgeWindow) - 1);
      commonInput(sim, eid, input, pressed);
      setHv(c, eid, V.scoped, input.buttons & Btn.Secondary ? 1 : 0);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(3);
        setHv(c, eid, V.plasmaCharge, 0.35);
      }
      if (c.Ult.active[eid]! > 0) {
        const charge = Math.min(1, hv(c, eid, V.plasmaCharge) + (0.65 / 0.6) * DT);
        setHv(c, eid, V.plasmaCharge, charge);
        if (!(input.buttons & Btn.Ultimate) || charge >= 1) {
          action(sim, eid, ActionKind.Ult, 18);
          const a = aim(sim, eid);
          const p = spawnProjectile(sim, eid, { kind: PK.Plasma, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 72), radius: 0.24 + charge * 0.14, seconds: 1.4, seq: input.seq });
          if (p !== undefined) c.Projectile.bounces[p] = Math.round(charge * 100);
          c.Ult.active[eid] = 0;
          setHv(c, eid, V.plasmaCharge, 0);
        }
      }
      if (pressed & Btn.Ability1 && fleet >= 24) {
        setHv(c, eid, V.fleet, fleet - 24);
        action(sim, eid, ActionKind.Cast, 8);
        const d = wishDir(input);
        startMove(sim, eid, MoveKind.Launch, 18, 16, { x: d.x * 14, y: 8.5, z: d.z * 14 });
      }
      if (pressed & Btn.Ability2) {
        if (hv(c, eid, V.edgeWindow) > 0) {
          action(sim, eid, ActionKind.Melee, 12);
          const a = aim(sim, eid, 12, false);
          startMove(sim, eid, MoveKind.Leap, 24, 28, a.point);
          kick(sim, eid, 3.2, 0.2);
          setHv(c, eid, V.edgeWindow, 0);
        } else if (ready(sim, eid, Slot.Ability2)) {
          consume(sim, eid, Slot.Ability2);
          action(sim, eid, ActionKind.Melee, 14);
          if (kick(sim, eid, 5)) setHv(c, eid, V.edgeWindow, secs(5));
        }
      }
      if (pressed & Btn.Ability3 && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 16);
        const a = targetPoint(sim, eid, Slot.Ability3);
        startMove(sim, eid, MoveKind.Leap, 26, 24, a.pos);
        const p = add(a.pos, { x: 0, y: 0.2, z: 0 });
        sim.emit({ type: "explosion", pos: p, radius: 5, kind: FX.Kick });
        for (const { eid: t } of playersInRadius(sim, p, 5, (e) => isEnemy(sim, eid, e))) applyDamage(sim, t, 30, eid, { pos: p });
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        const a = aim(sim, eid);
        const near = playersInRadius(sim, add(sim.posOf(eid), { x: 0, y: 0.9, z: 0 }), 3, (e) => isEnemy(sim, eid, e), false);
        if (near.length && !(input.buttons & Btn.Secondary)) {
          c.Weapon.fireCd[eid] = secs(0.5);
          action(sim, eid, ActionKind.Melee, 10);
          applyDamage(sim, near[0]!.eid, 45, eid);
        } else {
          c.Weapon.fireCd[eid] = secs(0.83);
          action(sim, eid, input.buttons & Btn.Secondary ? ActionKind.AltFire : ActionKind.Fire, 8);
          hitscan(sim, eid, a.origin, a.dir, {
            range: input.buttons & Btn.Secondary ? 120 : 60,
            damage: 120,
            falloffStart: input.buttons & Btn.Secondary ? undefined : 10,
            falloffEnd: input.buttons & Btn.Secondary ? undefined : 20,
            falloffMin: 0.5,
            canCrit: true,
            fxKind: FX.Rifle,
          });
        }
      }
    },
    movement(sim, eid, input, mods) {
      if ((input.buttons & Btn.Ability1) && hv(sim.c, eid, V.fleet) > 0) {
        setHv(sim.c, eid, V.fleet, Math.max(0, hv(sim.c, eid, V.fleet) - 12 * DT));
        mods.speedMul *= 1.33;
      }
      if (hv(sim.c, eid, V.scoped) > 0 || hv(sim.c, eid, V.plasmaCharge) > 0) mods.speedMul *= 0.55;
    },
  },
  projectiles: {
    [PK.Plasma]: simpleProjectile((sim, p, owner, hit) => {
      if (!ownerAlive(sim, owner)) return;
      const charge = Math.max(0.35, (sim.c.Projectile.bounces[p] || 100) / 100);
      const radius = 4 + 4 * charge;
      explode(sim, owner, hit.pos, { radius, damage: 90 + 90 * charge, minDamage: 35, fxKind: FX.Plasma, skip: hit.target });
      if (hit.target !== undefined) {
        applyDamage(sim, hit.target, 90 + 90 * charge, owner, { pos: hit.pos });
        applyStatus(sim, hit.target, "slow", 4, { slowPct: 0.55, source: owner });
      }
      for (let i = 0; i < 5; i++) {
        const a = i * 2.399963 + sim.c.Weapon.shots[owner]! * 0.17;
        const r = 0.8 + (i % 3) * 1.1;
        spawnZone(sim, owner, ZK.PlasmaPool, toGround(sim, { x: hit.pos.x + Math.cos(a) * r, y: hit.pos.y, z: hit.pos.z + Math.sin(a) * r }), 1.5 + charge, 4);
      }
      void p;
    }, { color: "#f28ab0", size: 0.2, trail: true }),
  },
  zones: {
    [ZK.PlasmaPool]: {
      visual: { color: "#ff4fa0", dome: false },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        for (const { eid: t } of playersInRadius(sim, add(sim.posOf(z), { x: 0, y: 0.4, z: 0 }), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), false)) {
          applyStatus(sim, t, "slow", 0.25, { slowPct: 0.55, source: owner });
        }
      },
    },
  },
  fx: {
    [FX.Rifle]: { color: "#ff9bc4" },
    [FX.Plasma]: { color: "#ff4fa0" },
    [FX.Kick]: { color: "#f28ab0" },
  },
  targets: {
    [Slot.Ability3]: { range: 18, radius: 5, verb: "Slam" },
  },
};

export default module;
